import { useEffect, useState, useRef, memo } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { useUploadContextFactory } from '../upload-contexts/use-upload-context-factory';
import GoBackButton from './go-back-button';
import { Trash2Icon } from 'lucide-react';
import { cn } from '@/lib/utils';
import Checkbox from '../checkbox';
import { Loader } from '@/components/loader';

const DataValidationTable = ({ contextType }) => {
  const {
    mappedData,
    validationSchema,
    properties,
    propertyOptions,
    goBack,
    propertiesMap,
    onSubmit,
    hasLocations,
    CustomPropsRender = null,
  } = useUploadContextFactory({ contextType });

  // remove location column if there are no locations
  const displayProperties = hasLocations
    ? properties
    : properties.filter(property => property !== 'location');
  const [showOnlyErrors, setShowOnlyErrors] = useState(false);
  const [loading, setLoading] = useState(false);

  const methods = useForm({
    resolver: zodResolver(validationSchema),
    defaultValues: { [contextType]: mappedData },
    mode: 'onBlur',
  });
  const { register, control, handleSubmit, trigger, formState } = methods;
  const { fields, remove } = useFieldArray({ control, name: contextType });
  const { errors } = formState;

  useEffect(() => {
    trigger();
  }, []);

  const innerOnSubmit = async data => {
    setLoading(true);
    await onSubmit(data);
    setLoading(false);
  };

  return (
    <div className="w-100">
      <GoBackButton onClick={goBack} />
      <form onSubmit={handleSubmit(innerOnSubmit)}>
        {mappedData.length > 0 && (
          <div className="border border-solid p-6 rounded-sm flex flex-col gap-6 w-100 overflow-y-scroll">
            <div className="w-100">
              <button
                type="submit"
                className="mb-4 border border-solid bg-slate-950 rounded-md text-white p-2 w-100 disabled:opacity-50"
                disabled={!mappedData.length || Object.keys(errors).length !== 0 || loading}>
                Submit
              </button>
              {CustomPropsRender}
            </div>
            {loading ? (
              <Loader />
            ) : (
              <div className="pb-12">
                <Checkbox
                  onChange={e => setShowOnlyErrors(e.target.checked)}
                  label={'Show only rows with errors'}
                />
                <table className="border border-solid">
                  <Header properties={displayProperties} propertyOptions={propertyOptions} />
                  <tbody>
                    {fields.map((user, index) => (
                      <tr
                        key={user.id}
                        className={cn('h-10 border', {
                          hidden: showOnlyErrors && !errors?.[contextType]?.[index],
                          'pointer-events-none': loading,
                          'cursor-not-allowed': loading,
                        })}>
                        <td>
                          <Trash2Icon
                            className={cn(
                              'cursor-pointer text-red-500 hover:text-red-700 justify-self-center',
                            )}
                            onClick={() => remove(index)}
                          />
                        </td>
                        <UserProperties
                          properties={displayProperties}
                          index={index}
                          control={control}
                          errors={errors}
                          professionalFieldOptions={propertiesMap}
                          register={register}
                          contextType={contextType}
                        />
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </form>
    </div>
  );
};

const Header = ({ properties, propertyOptions }) => (
  <thead>
    <tr>
      <th className="border border-solid p-4">Delete</th>
      {properties.map(property => (
        <th className="border border-solid p-4" key={property}>
          {propertyOptions[property]}
        </th>
      ))}
    </tr>
  </thead>
);

const UserProperties = ({
  properties,
  index,
  control,
  errors,
  professionalFieldOptions,
  register,
  contextType,
}) => (
  <>
    {properties.map(property => (
      <td key={property} className="p-0 border border-solid">
        <Controller
          name={`${contextType}.${index}.${property}`}
          control={control}
          render={({ field }) =>
            professionalFieldOptions[property] ? (
              <ProfessionalFieldSelect
                field={field}
                multiple={professionalFieldOptions[property].multiple}
                options={professionalFieldOptions[property].options}
                register={register}
                name={`${contextType}.${index}.${property}`}
                error={errors?.[contextType]?.[index]?.[property]}
              />
            ) : (
              <FieldInputWithError
                field={field}
                error={errors?.[contextType]?.[index]?.[property]}
                register={register}
                name={`${contextType}.${index}.${property}`}
              />
            )
          }
        />
      </td>
    ))}
  </>
);

const ProfessionalFieldSelect = memo(
  ({ field, options, name, register, multiple = false, error }) => {
    const [localSelectedValues, setLocalSelectedValues] = useState(
      Array.isArray(field.value)
        ? field.value.filter(val => options.some(opt => opt.key === val))
        : [],
    );
    const detailsRef = useRef(null);

    useEffect(() => {
      const handleClickOutside = event => {
        if (detailsRef.current && !detailsRef.current.contains(event.target)) {
          detailsRef.current.removeAttribute('open');
        }
      };
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    const toggleOption = optionKey => {
      // Filter out any invalid values and add/remove the new option
      const validValues = localSelectedValues.filter(val => options.some(opt => opt.key === val));
      const newSelectedValues = validValues.includes(optionKey)
        ? validValues.filter(v => v !== optionKey)
        : [...validValues, optionKey];

      setLocalSelectedValues(newSelectedValues);
      field.onChange(newSelectedValues);
      field.onBlur(); // Trigger validation
    };

    if (!multiple) {
      const isValueInOptions = field.value ? options.some(opt => opt.key === field.value) : false;

      return (
        <div className="relative group">
          <select
            {...register(name)}
            onChange={e => {
              field.onChange(e.target.value);
              field.onBlur();
            }}
            value={field.value || ''}
            name={name}
            className={cn(
              'rounded-none border-none p-3 mr-2',
              { 'text-red-500 border-red-500': error },
              { 'text-gray-900': !error },
            )}>
            <option value="" disabled>
              Select an option
            </option>
            {!isValueInOptions && field.value && <option value={field.value}>{field.value}</option>}
            {options.map(({ key, value }) => (
              <option key={key} value={key}>
                {value}
              </option>
            ))}
          </select>
        </div>
      );
    }

    return (
      <details ref={detailsRef} className="relative w-full">
        <summary
          className={cn(
            'bg-white p-3 w-full text-left flex justify-between items-center rounded cursor-pointer',
            {
              'text-red-500 border border-red-500': error,
            },
          )}>
          {localSelectedValues.length ? (
            <div className="flex justify-start gap-2 items-center flex-wrap min-w-80">
              {localSelectedValues.map(val => {
                const selectedOption = options.find(opt => opt.key === val);
                return selectedOption ? (
                  <span
                    key={val}
                    className={cn('border rounded px-2 py-1 text-sm min-w-fit', {
                      'bg-red-100 border-red-300': error,
                      'bg-gray-100 border-gray-300': !error,
                    })}>
                    {selectedOption.value}
                  </span>
                ) : null;
              })}
            </div>
          ) : (
            <span className={cn('text-gray-500', { 'text-red-500': error })}>
              {console.log(error)}
              {error
                ? error?.map(e => e.message.trim().replace('\n', '')).join(', ') || 'Invalid'
                : 'Select options'}
            </span>
          )}
          <svg className="w-4 h-4 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
            <path
              fillRule="evenodd"
              // the native arrow icon of the select to be consistent with the single select above
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </summary>

        <ul className="absolute z-10 bg-white shadow-md w-full max-h-48 overflow-y-auto border border-gray-200 rounded mt-1">
          {options.map(({ key, value }) => (
            <li
              key={key}
              className="flex items-center p-2 hover:bg-gray-100 cursor-pointer"
              onClick={() => toggleOption(key)}>
              <input
                type="checkbox"
                readOnly
                checked={localSelectedValues.includes(key)}
                className="mr-2"
              />
              <span className="text-sm truncate">{value}</span>
            </li>
          ))}
        </ul>
      </details>
    );
  },
);
ProfessionalFieldSelect.displayName = 'ProfessionalFieldSelect';

const FieldInputWithError = ({ field, error, register, name }) => {
  const inputElement = (
    <input
      value={field.value}
      onChange={e => field.onChange(e.target.value)}
      className={cn(
        `rounded-none border-none focus-visible:ring-0 transition-all duration-150 overflow-hidden h-100 p-3`,
        {
          'bg-red-500 text-white': error,
        },
      )}
      {...register(name)}
      type="text"
    />
  );

  return error ? (
    <div className="relative group">
      {inputElement}
      <div className="absolute hidden group-hover:block z-30 text-red-500 overflow-scroll bg-white border border-red-500 p-2">
        {error.message || 'Invalid input'}
      </div>
    </div>
  ) : (
    inputElement
  );
};

export default DataValidationTable;
